import { faRobot as IconeParticipacaoVirtual } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import RecordVoiceOver from '@mui/icons-material/RecordVoiceOver';
import VideoCameraFront from '@mui/icons-material/VideoCameraFront';
import SmartDisplayIcon from '@mui/icons-material/SmartDisplay';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import { Avatar } from '@mui/material';
import { cyan, deepOrange, grey, lime, purple } from '@mui/material/colors';
import SituacaoIdDescricaoBase from './SituacaoIdDescricaoBase';

/**
 * Enum para modos de participação de advogado.
 *
 * @readonly
 * @constant
 * @enum {Object.<string, Object>}
 */
// prettier-ignore
export const MODO_PARTICIPACAO_ADVOGADO = {
    INDEFINIDO                  : { id: 'INDEFINIDO',                  descricao: 'Indefinido',                  codigo: 'I',  cor: grey[700]       },
    PRESENCIAL                  : { id: 'PRESENCIAL',                  descricao: 'Presencial',                  codigo: 'P',  cor: cyan[800]       },
    TELEPRESENCIAL              : { id: 'TELEPRESENCIAL',              descricao: 'Por videoconferência',        codigo: 'T',  cor: purple[800]     },
    PEDIDO_DESTAQUE             : { id: 'PEDIDO_DESTAQUE',             descricao: 'Pedido de destaque',          codigo: 'D',  cor: deepOrange[700] },
    VIRTUAL                     : { id: 'VIRTUAL',                     descricao: 'Virtual',                     codigo: 'V',  cor: lime[700]       },
    SUSTENTACAO_ORAL_ELETRONICA : { id: 'SUSTENTACAO_ORAL_ELETRONICA', descricao: 'Sustentação oral eletrônica', codigo: 'S',  cor: lime[900]       }
};

/**
 * @classdesc Facilita o acesso às propriedades e valores da enum de modos de participação.
 * @readonly
 */
export default class ModoParticipacaoAdvogado {
    static situacaoBase = new SituacaoIdDescricaoBase(MODO_PARTICIPACAO_ADVOGADO);
    static MSG_ERRO_IDENTIFICACAO_MODO_PARTICIPACAO = 'Não foi possível identificar o modo de participação.';

    static getDescricao(id) {
        return ModoParticipacaoAdvogado.situacaoBase.getDescricao(id);
    }

    static getCodigo(id) {
        return ModoParticipacaoAdvogado.situacaoBase.getValorPropriedade(id, 'codigo');
    }

    static getCor(id) {
        return ModoParticipacaoAdvogado.situacaoBase.getValorPropriedade(id, 'cor');
    }

    static getIdByCodigo(codigo) {
        const obj = ModoParticipacaoAdvogado.situacaoBase.getObjectByProperty('codigo', codigo);

        if (!obj) {
            console.warn(`Modo de participação "${codigo}" desconhecido.`);
        }

        return obj ? obj.id : '';
    }

    static getDescricaoByCodigo(codigo) {
        return ModoParticipacaoAdvogado.getDescricao(ModoParticipacaoAdvogado.getIdByCodigo(codigo));
    }

    /**
     * Retorna o ícone correspondente ao ID do modo de participação.
     *
     * @param {string} id - O ID do modo de participação.
     * @param {string} [tamanhoIcone='28px'] - O tamanho do ícone.
     * @param {string} [cor=undefined] - A cor a ser utilizada no ícone (definir caso a queira indicar presença/ausência no próprio ícone).
     * @returns {Object} - Um elemento React
     */
    static getIcone(id, tamanhoIcone = '28px', cor = undefined) {
        switch (id) {
            case MODO_PARTICIPACAO_ADVOGADO.PRESENCIAL.id:
                return (
                    <RecordVoiceOver
                        sx={{ fontSize: tamanhoIcone, color: cor || MODO_PARTICIPACAO_ADVOGADO.PRESENCIAL.cor }}
                    />
                );
            case MODO_PARTICIPACAO_ADVOGADO.TELEPRESENCIAL.id:
                return (
                    <VideoCameraFront
                        sx={{ fontSize: tamanhoIcone, color: cor || MODO_PARTICIPACAO_ADVOGADO.TELEPRESENCIAL.cor }}
                    />
                );

            case MODO_PARTICIPACAO_ADVOGADO.VIRTUAL.id:
                return (
                    <FontAwesomeIcon
                        icon={IconeParticipacaoVirtual}
                        style={{ fontSize: tamanhoIcone, color: cor || MODO_PARTICIPACAO_ADVOGADO.VIRTUAL.cor }}
                    />
                );
            case MODO_PARTICIPACAO_ADVOGADO.SUSTENTACAO_ORAL_ELETRONICA.id:
                return (
                    <SmartDisplayIcon
                        sx={{
                            fontSize: tamanhoIcone,
                            color: cor || MODO_PARTICIPACAO_ADVOGADO.SUSTENTACAO_ORAL_ELETRONICA.cor
                        }}
                    />
                );
            case MODO_PARTICIPACAO_ADVOGADO.PEDIDO_DESTAQUE.id:
                return (
                    <BookmarkAddIcon
                        sx={{ fontSize: tamanhoIcone, color: cor || MODO_PARTICIPACAO_ADVOGADO.PEDIDO_DESTAQUE.cor }}
                    />
                );
            case MODO_PARTICIPACAO_ADVOGADO.INDEFINIDO.id:
            default:
                return (
                    <Avatar
                        style={{
                            width: tamanhoIcone,
                            height: tamanhoIcone,
                            fontWeight: '600',
                            fontSize: '0.96rem',
                            backgroundColor: cor || grey[700]
                        }}
                    >
                        ?
                    </Avatar>
                );
        }
    }

    /**
     * Retorna o ícone correspondente ao código do modo de participação.
     *
     * @param {string} codigo - O código do modo de participação.
     * @param {string} [tamanhoIcone='28px'] - O tamanho do ícone.
     * @param {string} [cor=undefined] - A cor a ser utilizada no ícone (definir caso a queira indicar presença/ausência no próprio ícone).
     * @returns {Object} - Um elemento React
     */
    static getIconeByCodigo(codigo, tamanhoIcone = '28px', cor = undefined) {
        return ModoParticipacaoAdvogado.getIcone(ModoParticipacaoAdvogado.getIdByCodigo(codigo), tamanhoIcone, cor);
    }
}
